//

import bulkActionsActionTypes, {
    fetchBulkActionActionTypes,
    postBulkActionActionTypes,
    fetchSafedSearchActionTypes,
    postSafedSearchActionTypes,
} from "shared/search/searchify/bulkactions/action-types"

// async imports
import { resourceGetDetail, resourcePostList } from "shared/djangio/action-creators"

// BulkActionsConsole unmount
export const disableBulkActionsMode = (uniqueFlvId) => ({
    type: bulkActionsActionTypes.DISABLE_BULK_ACTIONS_MODE,
    uniqueFlvId,
})

// BulkActionsConsole mount
export const initializeBulkActionsSlice = (uniqueFlvId, bulkActionValidOptions) => ({
    type: bulkActionsActionTypes.INITIALIZE_BULK_ACTIONS_SLICE,
    uniqueFlvId,
    bulkActionValidOptions,
})

// after the form successfully performs onSubmit()
export const clearBulkActionsSlice = (uniqueFlvId, bulkActionValidOptions) => ({
    type: bulkActionsActionTypes.CLEAR_BULK_ACTIONS_SLICE,
    uniqueFlvId,
    bulkActionValidOptions,
})

// BulkActionsSelectButton (Select All)
export const bulkActionsSelectAll = (uniqueFlvId, currentCount) => ({
    type: bulkActionsActionTypes.BULK_ACTIONS_SELECT_ALL,
    uniqueFlvId,
    currentCount,
})

// BulkActionsSelectButton (Clear)
export const clearBulkActionsSelections = (uniqueFlvId) => ({
    type: bulkActionsActionTypes.CLEAR_BULK_ACTIONS_SELECTIONS,
    uniqueFlvId,
})

// clicking an inline when selectIsBulkActionsSubmittable is true
export const toggleBulkActionInlineSelectedStatus = (uniqueFlvId, dataId, isInlineSelected) =>
    isInlineSelected
        ? // deselect ID if currently selected
          {
              type: bulkActionsActionTypes.DELETE_ID,
              id: dataId,
              uniqueFlvId,
          }
        : {
              type: bulkActionsActionTypes.ADD_ID,
              id: dataId,
              uniqueFlvId,
          }

export const updateSidebarButton = (uniqueFlvId, bulkActionType, bulkActionValue, reset) => (dispatch) => {
    // we use the pristine prop to determine whether or not to enable the Submit button
    reset()

    dispatch({
        type: bulkActionsActionTypes.UPDATE_SIDEBAR_BUTTON,
        uniqueFlvId,
        bulkActionType,
        bulkActionValue,
    })
}

export const createBulkActionsForm = (uniqueFlvId, bulkActionType) => ({
    type: bulkActionsActionTypes.CREATE_BULK_ACTIONS_FORM,
    uniqueFlvId,
    bulkActionType,
})

export const updateFormSelect = (uniqueFlvId, bulkActionType, argType, itemValue) => ({
    type: bulkActionsActionTypes.UPDATE_FORM_SELECT,
    uniqueFlvId,
    bulkActionType,
    argType,
    itemValue,
})

// async action creators
const { SafedSearch, QuorumUser } = DjangIO.app.userdata.models
const { BulkAction } = DjangIO.app.search.bulkactions.models

/**
 * This action creator fetches a SafedSearch
 *
 * @param {Number} bulkActionId - the id of the associated BulkAction
 *                            (this arg is passed as via the "payload"
 *                            and used in the reducer to store the
 *                            response)
 * @param {Number} id - the id of the SafedSearch to get
 * @returns {Promise} Promise with the resulting response
 */
export const fetchSafedSearch = ({ bulkActionId, id }) =>
    resourceGetDetail(SafedSearch, fetchSafedSearchActionTypes, id, { bulkActionId })

/**
 * This action creator fetches a BulkAction
 *
 * @param {Number} id - the id of the BulkAction to get
 * @returns {Promise} Promise with the resulting response
 */
export const fetchBulkAction = (id) =>
    resourceGetDetail(
        BulkAction.objects.filter({ full_dehydrate_safed_search: true }),
        fetchBulkActionActionTypes,
        id,
        { bulkActionId: id },
    )

/**
 * This action creator creates a SafedSearch.
 *
 * @param {Number} dataType - an integer corresponding to the QuorumDataType enum
 * @param {Object} addedIds - an object corresponding to the selected ids to be saved
 * @returns {Promise} Promise with the resulting response
 */
export const postSafedSearch = ({ dataType, addedIds, deletedIds, filters }) =>
    resourcePostList(SafedSearch, postSafedSearchActionTypes, {
        always_forced_regions: Userdata.current_regions_values,
        added_ids: addedIds,
        deleted_ids: deletedIds,
        data_type: dataType,
        filters: filters || [],
        name: "Auto Generated Bulk Action Safed Search",
        user: QuorumUser.foreignKey(parseInt(Userdata.id)),
    })

/**
 * This action creator creates a BulkAction.
 *
 * @param {Object} args - a mapping of args to pass to the QJSONField
 * @param {Number} bulkActionType - an integer corresponding to the BulkActionType
 * @param {Number} dataType - an integer corresponding to the QuorumDataType enum
 * @param {Number} safedSearchId - an integer corresponding to an id of a SafedSearch
 * @returns {Promise} Promise with the resulting response
 */
export const postBulkAction = ({ args, bulkActionType, dataType, safedSearchId }) =>
    resourcePostList(BulkAction, postBulkActionActionTypes, {
        args,
        bulk_action_type: bulkActionType,
        data_type: dataType,
        safed_search: SafedSearch.foreignKey(parseInt(safedSearchId)),
        user: QuorumUser.foreignKey(parseInt(Userdata.id)),
    })

/**
 * This creates a SafedSearch, then creates a BulkAction with a foreign key to that
 * SafedSearch. This function calls postSafedSearch then postBulkAction.
 *
 * @param {Number} bulkActionType - an integer corresponding to the BulkActionType enum
 * @param {Number} dataType - an integer corresponding to the QuorumDataType enum
 * @param {Object} addedIds - an object corresponding to the selected ids to be saved
 * @returns {Promise} Promise with the resulting response
 */
export const createBulkAction =
    ({ dataType, bulkActionType, args, addedIds, deletedIds, filters }) =>
    async (dispatch) => {
        // Create the SafedSearch
        const { id } = await dispatch(
            postSafedSearch({
                dataType,
                addedIds,
                deletedIds,
                filters,
            }),
        )
        // Create the BulkAction with a foreign key to the SafedSearch
        await dispatch(
            postBulkAction({
                args,
                bulkActionType,
                dataType,
                safedSearchId: id,
            }),
        )
    }
